import { createRef, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import {
    addExpertise,
    companyExpertiseSelector,
    fetchExpertise as fetchCompanyExpertise,
    removeExpertise,
} from 'store/company/expertise';

import {
    expertiseSelector,
    fetchExpertise as fetchGlobalExpertise,
} from 'store/expertise';

import styles from 'styles/modules/ProfileBio.module.scss';
import ExpertiseDropdown from 'components/ExpertiseDropdown';
import AddContent from './AddContent';
import Tags from 'components/Tags';

import { profile } from 'lang/en.json';
import { authSelector } from 'store/auth';
import { companySelector } from 'store/company';
import Typo from 'components/Typo';
import useFitCount from 'hooks/useFitCount';

// The chip may stretch to fill its row, so its natural size is the label plus
// its own padding, border and trailing margin.
const chipWidth = (chip) => {
    const style = getComputedStyle(chip);
    return [
        'paddingLeft',
        'paddingRight',
        'borderLeftWidth',
        'borderRightWidth',
        'marginRight',
    ].reduce(
        (width, property) => width + parseFloat(style[property]),
        chip.firstElementChild.scrollWidth
    );
};

const Expertise = () => {
    const dispatch = useDispatch();
    const { isAuthorized } = useSelector(authSelector);
    const { data: globalExpertise } = useSelector(expertiseSelector);
    const { id } = useSelector(companySelector).data;
    const { data, profileId } = useSelector(companyExpertiseSelector);
    const companyExpertise = profileId === id ? data : null;

    const [activeAdder, setActiveAdder] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const rowRef = useRef(null);
    const tagsRef = useRef(null);
    const moreRef = useRef(null);
    const visible = useFitCount({
        rowRef,
        buttonRef: moreRef,
        measure: () => [...tagsRef.current.children].map(chipWidth),
        total: companyExpertise?.length ?? 0,
        max: 5,
        key: companyExpertise?.map((tag) => tag.id).join(),
        enabled: !expanded,
    });

    useEffect(() => {
        const chip = tagsRef.current?.children[visible]?.firstElementChild;
        if (!expanded || !chip) return;
        // The chip label is a plain span, which can't take focus without this.
        chip.tabIndex = -1;
        chip.focus();
    }, [expanded]);

    useEffect(() => {
        if (id && profileId !== id) {
            dispatch(fetchCompanyExpertise(id));
        }
        if (!globalExpertise) {
            dispatch(fetchGlobalExpertise());
        }
    }, [id, profileId, globalExpertise]);

    if (!companyExpertise) {
        return null;
    }

    const tagRow = companyExpertise.length > 0 && (
        <div
            ref={rowRef}
            className={[
                styles.tagRow,
                expanded ? styles.tagRowExpanded : '',
            ].join(' ')}
        >
            <div ref={tagsRef} className={styles.tagRowList}>
                <Tags
                    data={{
                        activeTags: companyExpertise,
                        onClick: ({ id }) => {
                            dispatch(removeExpertise(id));
                        },
                        limit: companyExpertise.length,
                        visible: expanded ? companyExpertise.length : visible,
                    }}
                />
            </div>
            {!expanded && visible < companyExpertise.length && (
                <button
                    ref={moreRef}
                    type="button"
                    aria-expanded="false"
                    onClick={() => setExpanded(true)}
                    className={styles.loadMore}
                >
                    {`+${companyExpertise.length - visible} more`}
                </button>
            )}
        </div>
    );

    if (!isAuthorized) {
        return (
            <aside
                className={['mb-l', styles.list, styles.listOneRow].join(' ')}
                data-nosnippet
            >
                {tagRow || (
                    <p>
                        This company {"hasn't"} added any areas of expertise yet
                    </p>
                )}
            </aside>
        );
    }

    let adderRef = createRef();
    let dropdownData = {
        name: 'expertise',
        inline: false,
        items: globalExpertise || [],
        exclude: companyExpertise,
        callback: (item) => {
            if (
                companyExpertise.find(
                    (ce) => ce.id === item.id || ce.name === item.name
                )
            ) {
                return;
            }
            dispatch(addExpertise(item));
            // A new tag goes on the end, which a collapsed row would hide.
            setExpanded(true);
        },
    };

    if (!activeAdder) {
        dropdownData = {
            ...dropdownData,
            query: '',
            showList: false,
        };
    }

    return (
        <aside
            className={['mb-l', styles.list, styles.listOneRow].join(' ')}
            ref={adderRef}
        >
            {tagRow}

            <div
                className={[
                    styles.addTag,
                    activeAdder ? styles.addTagActive : null,
                ].join(' ')}
            >
                <div className={styles.dropdown}>
                    <ExpertiseDropdown data={dropdownData} />
                </div>

                <div className={styles.add}>
                    <AddContent
                        data={{
                            direction: 'row',
                            label:
                                activeAdder ||
                                (companyExpertise &&
                                    companyExpertise.length > 6)
                                    ? ''
                                    : profile.header.expertise.button,
                            active: activeAdder,
                            onClick: () => {
                                setActiveAdder(!activeAdder);
                            },
                        }}
                    />
                </div>
            </div>
        </aside>
    );
};

export default Expertise;
